import { timeZoneOf } from './markets';

/**
 * Când pleacă mailul de dimineață: în zilele lucrătoare, la 7:50 ora firmei
 * (programul începe la 8, iar mailul nostru trebuie să fie printre primele).
 * Dacă serverul n-a prins fereastra, ziua aceea nu se mai recuperează: un
 * reminder de dimineață primit la prânz și-a pierdut rostul.
 */

/** 7:50, în minute de la miezul nopții */
export const MORNING_AT = 7 * 60 + 50;
/** cât timp după 7:50 încă trimitem (repornire, coadă) */
export const MORNING_WINDOW = 60;

const DAY = 86_400_000;
const iso = (t: number): string => new Date(t).toISOString().slice(0, 10);
const plus = (day: string, n: number): string => iso(Date.parse(`${day}T00:00:00Z`) + n * DAY);

/** Paștele ortodox, în calendarul nou (calcul iulian + 13 zile, valabil 1900–2099). */
export function orthodoxEaster(year: number): string {
  const a = year % 4;
  const b = year % 7;
  const c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31);
  const day = ((d + e + 114) % 31) + 1;
  return plus(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, 13);
}

/** Sărbătorile legale din România (Codul muncii, art. 139), pe un an. */
function romanianHolidays(year: number): Set<string> {
  const fixed = [
    '01-01',
    '01-02',
    '01-06',
    '01-07',
    '01-24',
    '05-01',
    '06-01',
    '08-15',
    '11-30',
    '12-01',
    '12-25',
    '12-26',
  ];
  const easter = orthodoxEaster(year);
  return new Set([
    ...fixed.map((md) => `${year}-${md}`),
    plus(easter, -2), // Vinerea Mare
    easter,
    plus(easter, 1),
    plus(easter, 49), // Rusaliile
    plus(easter, 50),
  ]);
}

/** Sărbătorile pe țară; o țară fără listă are doar weekendul liber, până o adăugăm. */
const HOLIDAYS: Record<string, (year: number) => Set<string>> = { RO: romanianHolidays };

export function isWorkday(day: string, country: string): boolean {
  const wd = new Date(`${day}T12:00:00Z`).getUTCDay();
  if (wd === 0 || wd === 6) return false;
  const list = HOLIDAYS[country];
  return !list || !list(Number(day.slice(0, 4))).has(day);
}

/** Ziua și ora (în minute) dintr-un fus orar, oricare ar fi fusul serverului. */
export function localClock(now: Date, timeZone: string): { day: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const get = (t: string): string => parts.find((p) => p.type === t)!.value;
  return {
    day: `${get('year')}-${get('month')}-${get('day')}`,
    minutes: Number(get('hour')) * 60 + Number(get('minute')),
  };
}

/** Ziua (ora firmei) pentru care e datorat mailul acum, sau null. */
export function morningDue(now: Date, country: string, lastDay: string | null): string | null {
  const { day, minutes } = localClock(now, timeZoneOf(country));
  if (minutes < MORNING_AT || minutes >= MORNING_AT + MORNING_WINDOW) return null;
  if (lastDay === day || !isWorkday(day, country)) return null;
  return day;
}

/**
 * Cine primește mailul de dimineață: oamenii de la Achiziții. Aprobatorul doar
 * când e singur în firmă (`colleagues` = oamenii activi din cont, fără cei de
 * pe șantier): atunci tot el cumpără.
 */
export const morningRecipient = (role: string, colleagues: number): boolean =>
  role === 'buyer' || (role === 'approver' && colleagues === 1);
